import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { createAzulPaymentSession, getRegistrationForRestore } from "../lib/api";
import { AzulRedirect } from "../components/AzulRedirect";

const BRAND = { navy: "#161b3d", maroon: "#8b1e22" };

/**
 * Public payment page: pay for a registration using Azul Hosted Page.
 * Creates session, then auto-POSTs to Azul; user returns via azulReturnHandler to /pay/azul/approved or /pay/azul/declined.
 * "Volver" links use student code (returnCode query or from API) so URL stays /pagos/{studentCode}.
 */
export function PayByRegistrationId() {
  const { registrationId } = useParams<{ registrationId: string }>();
  const [searchParams] = useSearchParams();
  const returnCode = searchParams.get("returnCode");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<{ actionUrl: string; formFields: Record<string, string> } | null>(null);
  const [studentCodeForBack, setStudentCodeForBack] = useState<string | null>(null);
  // Refs persist across React StrictMode's intentional double-mount, so storing
  // the in-flight promise here ensures only one backend call is made per rid.
  const sessionPromiseRef = useRef<Promise<{ actionUrl: string; formFields: Record<string, string> }> | null>(null);
  const sessionRidRef = useRef<string>("");

  const rid = registrationId ?? "";
  const pagosBackUrl = returnCode ?? studentCodeForBack ? `/pagos/${encodeURIComponent(returnCode ?? studentCodeForBack ?? "")}` : "/";

  useEffect(() => {
    if (!rid) {
      setError("Falta ID de inscripción.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    // Start a new session request only if rid changed or no request is in flight.
    if (sessionRidRef.current !== rid || !sessionPromiseRef.current) {
      sessionRidRef.current = rid;
      sessionPromiseRef.current = createAzulPaymentSession(rid);
    }

    sessionPromiseRef.current
      .then((res) => {
        if (cancelled) return;
        setSession({ actionUrl: res.actionUrl, formFields: res.formFields });
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "No se pudo iniciar el pago.");
        }
        sessionPromiseRef.current = null; // allow retry on error
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [rid]);

  // Resolve student code for "Volver" when not passed as returnCode (e.g. opened from admin link)
  useEffect(() => {
    if (!rid || returnCode) return;
    getRegistrationForRestore(rid)
      .then((res) => {
        if (res.studentCode) setStudentCodeForBack(res.studentCode);
      })
      .catch((err) => console.warn("[audit]", err));
  }, [rid, returnCode]);

  if (!rid) {
    return (
      <div className="min-h-screen bg-[#f5f5f7] flex items-center justify-center p-6">
        <div className="text-center">
          <p className="text-[15px] font-medium text-red-600">Falta ID de inscripción.</p>
          <Link
            to="/"
            className="mt-5 inline-block text-[14px] font-semibold transition-colors hover:opacity-80"
            style={{ color: BRAND.navy }}
          >
            Volver al inicio
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f5f7] flex items-center justify-center p-6">
        <div className="text-center">
          <div
            className="animate-spin rounded-full h-10 w-10 border-2 border-[#d2d2d7] border-t-[#161b3d] mx-auto"
            aria-hidden
          />
          <p className="mt-4 text-[13px] font-medium text-[#86868b]">Preparando pago…</p>
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="min-h-screen bg-[#f5f5f7] flex items-center justify-center p-6">
        <div className="max-w-sm w-full bg-white rounded-3xl border border-gray-100 shadow-[0_8px_40px_rgba(0,0,0,0.06)] p-8 text-center">
          <p className="text-[15px] font-medium text-red-600">
            {error ?? "No se pudo iniciar el pago."}
          </p>
          <div className="mt-6">
            <Link
              to={pagosBackUrl}
              className="block text-[14px] font-semibold transition-colors hover:opacity-80"
              style={{ color: BRAND.navy }}
            >
              Volver
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f5f7] flex flex-col items-center justify-center p-4 py-10">
      <div className="w-full max-w-[440px] bg-white rounded-[28px] border border-gray-100 shadow-[0_32px_64px_rgba(0,0,0,0.08)] overflow-hidden">
        <AzulRedirect
          actionUrl={session.actionUrl}
          formFields={session.formFields}
          message="Redirigiendo a la pasarela de pago Azul…"
        />
      </div>
      <Link
        to={pagosBackUrl}
        className="mt-6 flex items-center justify-center gap-1.5 text-[14px] font-medium text-[#86868b] hover:text-[#1d1d1f] transition-colors"
      >
        <span aria-hidden>←</span>
        Volver a Mis clases
      </Link>
    </div>
  );
}
